// frontend/src/scenes/admin/Users.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, Grid, TextField, Typography, MenuItem, Alert, Snackbar, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import AdminBackButton from "./AdminBackButton";
import { authDelete, authGet, authPost, authPut, getUser } from "../../lib/authApi";

function pickRows(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.users)) return resp.users;
  return [];
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ email: "", role: "user", org_id: "" });

  const currentUser = getUser();
  const currentRole = String(currentUser?.role || localStorage.getItem("role") || "").toLowerCase();
  const isPlatformManager = ["owner", "global_admin", "superadmin"].includes(currentRole);
  const editableRoles = isPlatformManager ? ["admin", "reader", "user"] : ["reader", "user"];
  const canManage = (row) => {
    if (!row || String(row.id) === String(currentUser?.id || "")) return false;
    if (isPlatformManager) return ["admin", "reader", "user"].includes(String(row.role || "").toLowerCase());
    return currentRole === "admin" && ["user", "reader"].includes(String(row.role || "").toLowerCase());
  };

  const [form, setForm] = useState({
    email: "",
    password: "",
    role: "user",
    org_id: "",
    credential_mode: "welcome",
  });

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const [u, o, pending] = await Promise.all([authGet("/api/admin/users"), authGet("/api/admin/organizations"), authGet("/api/invite/pending")]);
      setUsers(pickRows(u));
      setOrgs(pickRows(o));
      setInvitations(pickRows(pending?.invitations || pending));
    } catch (e) {
      setErr(e.message || "Failed to load admin data.");
      setUsers([]);
      setInvitations([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createUser = async () => {
    setBusy(true);
    setErr("");
    try {
      const email = (form.email || "").trim().toLowerCase();
      const password = form.password || "";
      if (!email) throw new Error("Email is required.");
      if (!form.org_id) throw new Error("Pick an organization to assign.");
      if (!["admin", "reader", "user"].includes(form.role)) throw new Error("Only non-privileged organization roles can be created here.");
      if (form.credential_mode === "welcome") {
        await authPost("/api/invite", { email, role: form.role, org_id: form.org_id });
        setToast({ open: true, msg: "Welcome email sent. The invitation remains pending until completed.", severity: "success" });
      } else {
        if (!password || password.length < 10) throw new Error("Password must be at least 10 characters.");
        await authPost("/api/admin/users", { email, password, role: form.role, org_id: form.org_id });
        setToast({ open: true, msg: "User created with a password.", severity: "success" });
      }
      setForm({ email: "", password: "", role: "user", org_id: form.org_id, credential_mode: form.credential_mode });
      await load();
    } catch (e) {
      setErr(e.message || "Create failed.");
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    try {
      if (!passwordTarget) return;
      if (newPassword.length < 10) throw new Error("Password must be at least 10 characters.");
      setBusy(true);
      await authPut(`/api/admin/users/${passwordTarget.id}/password`, { password: newPassword });
      setPasswordTarget(null);
      setNewPassword("");
      setToast({ open: true, msg: "Password reset.", severity: "success" });
    } catch (e) {
      setErr(e.message || "Password reset failed.");
    } finally {
      setBusy(false);
    }
  };

  const openEditUser = (row) => {
    setEditTarget(row);
    setEditForm({
      email: String(row.email || ""),
      role: editableRoles.includes(String(row.role || "").toLowerCase()) ? String(row.role).toLowerCase() : "user",
      org_id: String(row.org_id || ""),
    });
  };

  const updateUser = async () => {
    try {
      if (!editTarget) return;
      const email = editForm.email.trim().toLowerCase();
      if (!email) throw new Error("Email is required.");
      if (!editableRoles.includes(editForm.role)) throw new Error("That role is not permitted for your account.");
      if (!editForm.org_id) throw new Error("Pick an organization to assign.");
      setBusy(true);
      setErr("");
      await authPut(`/api/admin/users/${editTarget.id}`, { email, role: editForm.role, org_id: editForm.org_id });
      setEditTarget(null);
      setToast({ open: true, msg: "User details updated.", severity: "success" });
      await load();
    } catch (e) {
      setErr(e.message || "User update failed.");
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async (row) => {
    if (!window.confirm(`Delete ${row.email}? This permanently removes their account and cannot be undone.`)) return;
    try {
      setBusy(true);
      setErr("");
      await authDelete(`/api/admin/users/${row.id}`);
      setToast({ open: true, msg: "User account deleted.", severity: "success" });
      await load();
    } catch (e) {
      setErr(e.message || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const resendWelcome = async (row) => {
    try {
      setBusy(true); setErr("");
      await authPost(`/api/invite/${row.invitation_id}/resend`, {});
      setToast({ open: true, msg: `Welcome email resent to ${row.email}.`, severity: "success" });
      await load();
    } catch (e) { setErr(e.message || "Unable to resend welcome email."); } finally { setBusy(false); }
  };

  const revokeInvitation = async (row) => {
    if (!window.confirm(`Revoke the pending welcome invitation for ${row.email}?`)) return;
    try {
      setBusy(true); setErr("");
      await authPost(`/api/invite/${row.invitation_id}/revoke`, {});
      setToast({ open: true, msg: "Welcome invitation revoked.", severity: "success" });
      await load();
    } catch (e) { setErr(e.message || "Unable to revoke invitation."); } finally { setBusy(false); }
  };

  const displayRows = useMemo(() => [
    ...users.map((row) => ({ ...row, invitation_status: "Active" })),
    ...invitations.map((row) => ({ id: `invite-${row.id}`, invitation_id: row.id, email: row.email, role: row.role, org_id: row.org_id, created_at: row.created_at, invitation_status: row.delivery === "sent" ? "Pending — sent" : "Pending", pending_invitation: true })),
  ], [users, invitations]);

  const columns = [
      { field: "id", headerName: "ID", flex: 1, minWidth: 220 },
      { field: "email", headerName: "Email", flex: 1, minWidth: 240 },
      { field: "role", headerName: "Role", width: 120 },
      { field: "invitation_status", headerName: "Access status", width: 150 },
      { field: "org_name", headerName: "Org", flex: 1, minWidth: 180 },
      { field: "org_id", headerName: "Org ID", flex: 1, minWidth: 220 },
      { field: "created_at", headerName: "Created", width: 220 },
      {
        field: "actions",
        headerName: "Actions",
        width: 290,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => row.pending_invitation ? (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%" }}>
            <Button size="small" onClick={() => resendWelcome(row)}>Resend welcome email</Button>
            <Button size="small" color="error" onClick={() => revokeInvitation(row)}>Revoke invitation</Button>
          </Box>
        ) : canManage(row) ? (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%" }}>
            <Button size="small" onClick={() => openEditUser(row)}>Edit</Button>
            <Button size="small" onClick={() => { setPasswordTarget(row); setNewPassword(""); }}>Reset password</Button>
            <Button size="small" color="error" onClick={() => deleteUser(row)}>Delete</Button>
          </Box>
        ) : null,
      },
    ];

  return (
    <Box p={3} sx={{ pb: 6 }}>
      <AdminBackButton />
      <Header title="Users" subtitle="Manage Equilibrium Scientific users and welcome invitations" />

      {err ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Add User
              </Typography>

              <TextField
                label="Email"
                fullWidth
                size="small"
                sx={{ mb: 1 }}
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              />

              <TextField
                select
                label="Credential choice"
                fullWidth
                size="small"
                sx={{ mb: 1 }}
                value={form.credential_mode}
                onChange={(e) => setForm((s) => ({ ...s, credential_mode: e.target.value, password: "" }))}
              >
                <MenuItem value="welcome">Send welcome email</MenuItem>
                <MenuItem value="password">Set password now</MenuItem>
              </TextField>

              {form.credential_mode === "password" ? <TextField
                label="Password"
                type="password"
                fullWidth
                size="small"
                sx={{ mb: 1 }}
                value={form.password}
                onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                helperText="Min 10 chars (matches backend requirement)."
              /> : <Alert severity="info" sx={{ mb: 1 }}>No initial password is collected or displayed. A controlled Equilibrium Scientific welcome invitation will be sent after the form is valid.</Alert>}

              <TextField
                select
                label="Role"
                fullWidth
                size="small"
                sx={{ mb: 1 }}
                value={form.role}
                onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))}
              >
                <MenuItem value="admin">admin</MenuItem>
                <MenuItem value="reader">reader</MenuItem>
                <MenuItem value="user">user</MenuItem>
              </TextField>

              <TextField
                select
                label="Assign Organization"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                value={form.org_id}
                onChange={(e) => setForm((s) => ({ ...s, org_id: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {orgs.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>

              <Button variant="contained" onClick={createUser} disabled={busy}>
                {form.credential_mode === "welcome" ? "Send welcome email" : "Create user"}
              </Button>
              <Button variant="text" onClick={load} disabled={busy} sx={{ ml: 1 }}>
                Refresh
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1">What this does</Typography>
              <Typography variant="body2" color="text.secondary">
                Creates a backend-auth user and assigns them to the selected organization. The backend route supports either:
                <br />• users.org_id schema, or
                <br />• a join table schema (auto-detected).
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ height: 520, width: "100%" }}>
            <DataGrid rows={displayRows} columns={columns} loading={busy} getRowId={(r) => r.id} disableRowSelectionOnClick />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={Boolean(editTarget)} onClose={busy ? undefined : () => setEditTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Email"
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 1 }}
            value={editForm.email}
            onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))}
          />
          <TextField
            select
            label="Role"
            fullWidth
            size="small"
            sx={{ mb: 1 }}
            value={editForm.role}
            onChange={(e) => setEditForm((s) => ({ ...s, role: e.target.value }))}
          >
            {editableRoles.map((role) => <MenuItem key={role} value={role}>{role}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Assign Organization"
            fullWidth
            size="small"
            value={editForm.org_id}
            onChange={(e) => setEditForm((s) => ({ ...s, org_id: e.target.value }))}
          >
            <MenuItem value="">—</MenuItem>
            {orgs.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !editForm.email || !editForm.org_id} onClick={updateUser}>Save changes</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(passwordTarget)} onClose={busy ? undefined : () => { setPasswordTarget(null); setNewPassword(""); }} fullWidth maxWidth="xs">
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a new password for {passwordTarget?.email}. They will need it at their next sign-in.
          </Typography>
          <TextField
            autoFocus
            label="New password"
            type="password"
            fullWidth
            size="small"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="At least 10 characters."
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => { setPasswordTarget(null); setNewPassword(""); }}>Cancel</Button>
          <Button variant="contained" disabled={busy || newPassword.length < 10} onClick={resetPassword}>Reset password</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}